import {
  Component,
  computed,
  DestroyRef,
  ElementRef,
  effect,
  HostListener,
  inject,
  viewChild,
} from '@angular/core';
import { DOCUMENT } from '@angular/common';
import {
  ActivatedRoute,
  NavigationEnd,
  Router,
  RouterOutlet,
} from '@angular/router';
import { MenuTabsComponent } from '@shared/components/menu-tabs/menu-tabs.component';
import { ToastDrawerComponent } from 'ngx-mt-ui-lib/components/toast/toast-drawer';
import { toSignal } from '@angular/core/rxjs-interop';
import {
  animationFrameScheduler,
  concat,
  defer,
  distinctUntilChanged,
  filter,
  fromEvent,
  map,
  merge,
  Observable,
  of,
  scheduled,
  startWith,
  switchMap,
  timer,
} from 'rxjs';
import { ThemeService } from '@core/services/theme.service';
import { TranslateService } from '@ngx-translate/core';
import { Title } from '@angular/platform-browser';
import { BottomChromeService } from '@core/services/bottom-chrome.service';
import { AppFocusRestoreService } from '@core/services/app-focus-restore.service';
import { AppNavigationContextService } from '@core/services/app-navigation-context.service';
import {
  CreateEventFabComponent,
  CreateEventFabRequest,
} from '@shared/components/create-event-fab/create-event-fab.component';
import { EventCreationDialogService } from '@app/features/add-security-event/event-creation-dialog.service';
import { AppCapabilityService } from '@core/services/app-capability.service';
import { Store } from '@ngrx/store';
import { selectAuth } from '@app/features/auth/auth.selectors';
import { OverlayContainer } from '@angular/cdk/overlay';

const NATIVE_UI_RETURN_SETTLE_DELAY_MS = 50;
const VISIBLE_VIEWPORT_HEIGHT_PROPERTY = '--app-visible-viewport-height';

@Component({
  selector: 'app-root',
  imports: [
    RouterOutlet,
    ToastDrawerComponent,
    MenuTabsComponent,
    CreateEventFabComponent,
  ],
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
  host: {
    '[style.--app-visible-viewport-height]': 'visibleViewportHeight()',
  },
})
export class AppComponent {
  private readonly document = inject(DOCUMENT);
  private readonly destroyRef = inject(DestroyRef);
  private readonly router = inject(Router);
  private readonly themeService = inject(ThemeService);
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly translateService = inject(TranslateService);
  private readonly titleService = inject(Title);
  private readonly bottomChrome = inject(BottomChromeService);
  private readonly navigationContext = inject(AppNavigationContextService);
  private readonly eventCreationDialog = inject(EventCreationDialogService);
  private readonly capabilities = inject(AppCapabilityService);
  private readonly auth = inject(Store).selectSignal(selectAuth);
  private readonly overlayContainer = inject(OverlayContainer);
  private readonly focusRestore = inject(AppFocusRestoreService);
  private readonly initialDocumentViewportHeight =
    this.document.documentElement.style.getPropertyValue(
      VISIBLE_VIEWPORT_HEIGHT_PROPERTY,
    );
  private readonly shellElement = viewChild<ElementRef<HTMLElement>>('shell');
  private sessionPendingFocus: HTMLElement | null = null;
  private sessionPendingFocusKey: string | null = null;
  private sessionFocusRestoreTimer: ReturnType<typeof setTimeout> | null = null;
  private sessionOverlayObserver: MutationObserver | null = null;
  private readonly sessionPendingPanes = new Map<
    HTMLElement,
    { readonly inert: boolean; readonly ariaHidden: string | null }
  >();
  private sessionStatusElement: HTMLElement | null = null;

  private readonly darkThemeOn = [
    '/auth-code',
    '/not-found',
    '/forbidden',
    '/access-unavailable',
    '/security-administrator',
  ];
  private readonly navigationEnd$ = this.router.events.pipe(
    filter((event): event is NavigationEnd => event instanceof NavigationEnd),
  );
  private readonly navigationEnd = toSignal(this.navigationEnd$);

  protected readonly visibleViewportHeight = toSignal(
    this.observeVisibleViewportHeight(),
    { initialValue: null },
  );

  private readonly routeEnablesBottomChrome = computed(() => {
    const _ = this.navigationEnd();
    let route: ActivatedRoute | null = this.activatedRoute;
    let enabled = false;

    while (route) {
      const routeSetting = route.snapshot?.data?.['bottomChrome'];
      if (typeof routeSetting === 'boolean') {
        enabled = routeSetting;
      }
      route = route.firstChild;
    }

    return enabled;
  });

  private readonly routeEnablesCreateEventFab = computed(() => {
    const _ = this.navigationEnd();
    let route: ActivatedRoute | null = this.activatedRoute;
    let enabled = true;

    while (route) {
      const routeSetting = route.snapshot?.data?.['createEventFab'];
      if (typeof routeSetting === 'boolean') {
        enabled = routeSetting;
      }
      route = route.firstChild;
    }

    return enabled;
  });

  protected readonly bottomChromeVisible = this.bottomChrome.visible;
  protected readonly activeContext = this.navigationContext.activeContext;
  protected readonly sessionSynchronizing = computed(
    () => this.auth().sessionTransitioning,
  );
  protected readonly createEventFabVisible = computed(
    () =>
      !this.sessionSynchronizing() &&
      this.bottomChromeVisible() &&
      this.routeEnablesCreateEventFab() &&
      this.activeContext() !== 'chat' &&
      this.capabilities.canCreateEvent(),
  );

  protected readonly title = computed(() => {
    const _ = this.navigationEnd();
    let child = this.activatedRoute?.firstChild ?? null;

    while (child?.firstChild) {
      child = child.firstChild;
    }

    if (child?.snapshot?.data['titleKey']) {
      return this.translateService.instant(child.snapshot.data['titleKey']);
    }

    if (child?.snapshot?.data['title']) {
      return child.snapshot.data['title'];
    }

    return null;
  });

  constructor() {
    this.destroyRef.onDestroy(() => {
      this.finishSessionIsolation(false);
      this.setDocumentViewportHeight(this.initialDocumentViewportHeight);
    });
    effect(() => {
      this.setDocumentViewportHeight(
        this.visibleViewportHeight() ?? this.initialDocumentViewportHeight,
      );
    });
    effect(() => {
      const title = this.title();
      this.titleService.setTitle(title);
    });
    effect(() => {
      this.bottomChrome.setRouteEnabled(this.routeEnablesBottomChrome());
    });
    effect(() => {
      if (this.sessionSynchronizing()) {
        this.startSessionIsolation();
      } else {
        this.finishSessionIsolation(true);
      }
    });
    effect(() => {
      const _ = this.navigationEnd();
      const url = this.router.url;

      const isDarkTheme = this.darkThemeOn.some((path) => url.includes(path));

      if (isDarkTheme) {
        this.themeService.setDarkTheme();
      } else {
        this.themeService.setUserTheme();
      }
    });
  }

  protected openEventCreation(request: CreateEventFabRequest): void {
    void this.eventCreationDialog.open(request);
  }

  @HostListener('document:pointerup', ['$event'])
  protected releaseTouchButtonFocus(event: PointerEvent): void {
    if (event.pointerType !== 'touch' && event.pointerType !== 'pen') {
      return;
    }

    const target = event.target;
    if (!(target instanceof Element)) {
      return;
    }

    const button = target.closest('button');
    this.document.defaultView?.setTimeout(() => {
      if (button && this.document.activeElement === button) {
        button.blur();
      }
    });
  }

  // После возврата из нативного UI layout viewport может отставать от VisualViewport.
  // Дополнительный замер ограничен событиями возврата, чтобы resize клавиатуры оставался синхронным.
  private observeVisibleViewportHeight(): Observable<string | null> {
    return defer(() => {
      const appWindow = this.document.defaultView;
      if (!appWindow) {
        return of(null);
      }

      const viewportEvents: Observable<unknown>[] = [
        this.navigationEnd$,
        fromEvent(appWindow, 'resize'),
      ];
      const visualViewport = appWindow.visualViewport;
      if (visualViewport) {
        viewportEvents.push(
          fromEvent(visualViewport, 'resize'),
          fromEvent(visualViewport, 'scroll'),
        );
      }

      const nativeUiReturn$ = merge(
        fromEvent(appWindow, 'pageshow'),
        fromEvent(appWindow, 'focus'),
        fromEvent(this.document, 'visibilitychange').pipe(
          filter(() => this.document.visibilityState === 'visible'),
        ),
      );

      return merge(
        merge(...viewportEvents).pipe(
          startWith(null),
          switchMap(() =>
            concat(of(null), scheduled([null], animationFrameScheduler)),
          ),
        ),
        nativeUiReturn$.pipe(
          switchMap(() =>
            merge(
              of(null),
              scheduled([null], animationFrameScheduler),
              timer(NATIVE_UI_RETURN_SETTLE_DELAY_MS),
            ),
          ),
        ),
      ).pipe(
        map(() => this.calculateVisibleViewportHeight(appWindow)),
        distinctUntilChanged(),
      );
    });
  }

  private calculateVisibleViewportHeight(appWindow: Window): string | null {
    const viewport = appWindow.visualViewport;
    if (viewport && viewport.scale <= 1.05 && viewport.height > 0) {
      const visibleBottom = viewport.height + Math.max(0, viewport.offsetTop);
      return `${Math.floor(visibleBottom)}px`;
    }

    return appWindow.innerHeight > 0
      ? `${Math.floor(appWindow.innerHeight)}px`
      : null;
  }

  private setDocumentViewportHeight(value: string): void {
    const style = this.document.documentElement.style;
    if (value) {
      style.setProperty(VISIBLE_VIEWPORT_HEIGHT_PROPERTY, value);
    } else {
      style.removeProperty(VISIBLE_VIEWPORT_HEIGHT_PROPERTY);
    }
  }

  private startSessionIsolation(): void {
    if (this.sessionOverlayObserver) {
      this.isolateOverlayPanes();
      return;
    }

    if (this.sessionFocusRestoreTimer) {
      clearTimeout(this.sessionFocusRestoreTimer);
      this.sessionFocusRestoreTimer = null;
    }
    const shell = this.shellElement()?.nativeElement;
    const overlay = this.overlayContainer.getContainerElement();
    const activeElement = this.document.activeElement;
    this.sessionPendingFocus =
      activeElement instanceof HTMLElement &&
      (shell?.contains(activeElement) || overlay.contains(activeElement))
        ? activeElement
        : null;
    this.sessionPendingFocusKey =
      activeElement === null ? null : this.focusRestore.keyFor(activeElement);

    overlay.classList.add('app-session-synchronizing');
    this.isolateOverlayPanes();
    const MutationObserverType = this.document.defaultView?.MutationObserver;
    if (MutationObserverType) {
      this.sessionOverlayObserver = new MutationObserverType(() =>
        this.isolateOverlayPanes(),
      );
      this.sessionOverlayObserver.observe(overlay, {
        childList: true,
        subtree: true,
      });
    }

    const status = this.document.createElement('div');
    status.className = 'app-session-status';
    status.setAttribute('role', 'status');
    status.setAttribute('aria-live', 'polite');
    status.textContent = this.translateService.instant('GENERAL.LOADER_TITLE');
    this.document.body.appendChild(status);
    this.sessionStatusElement = status;
  }

  private isolateOverlayPanes(): void {
    const overlay = this.overlayContainer.getContainerElement();
    for (const pane of Array.from(
      overlay.querySelectorAll<HTMLElement>('.cdk-overlay-pane'),
    )) {
      if (!this.sessionPendingPanes.has(pane)) {
        this.sessionPendingPanes.set(pane, {
          inert: pane.inert,
          ariaHidden: pane.getAttribute('aria-hidden'),
        });
      }
      pane.inert = true;
      pane.setAttribute('aria-hidden', 'true');
    }
  }

  private finishSessionIsolation(restoreFocus: boolean): void {
    this.sessionOverlayObserver?.disconnect();
    this.sessionOverlayObserver = null;
    const overlay = this.overlayContainer.getContainerElement();
    overlay.classList.remove('app-session-synchronizing');

    for (const [pane, state] of this.sessionPendingPanes) {
      pane.inert = state.inert;
      if (state.ariaHidden === null) {
        pane.removeAttribute('aria-hidden');
      } else {
        pane.setAttribute('aria-hidden', state.ariaHidden);
      }
    }
    this.sessionPendingPanes.clear();
    this.sessionStatusElement?.remove();
    this.sessionStatusElement = null;

    const previousFocus = this.sessionPendingFocus;
    const focusKey = this.sessionPendingFocusKey;
    this.sessionPendingFocus = null;
    this.sessionPendingFocusKey = null;
    if (!restoreFocus || (!previousFocus && !focusKey)) {
      return;
    }

    this.sessionFocusRestoreTimer = setTimeout(() => {
      this.sessionFocusRestoreTimer = null;
      const focusTarget = this.resolveSessionFocusTarget(
        previousFocus,
        focusKey,
      );
      if (focusTarget?.isConnected) {
        focusTarget.focus({ preventScroll: true });
      }
    });
  }

  private resolveSessionFocusTarget(
    previousFocus: HTMLElement | null,
    focusKey: string | null,
  ): HTMLElement | null {
    if (previousFocus?.isConnected) {
      return previousFocus;
    }

    return focusKey ? this.focusRestore.resolve(focusKey) : null;
  }
}
